import {ReactElement, useEffect, useState} from "react";
import {EditorGroup, EditorGroupTitle} from "../EditorGroup";
import {FormattedMessage, FormattedNumber} from "react-intl";
import {assoc, assocPath, identity, isEmpty, range} from "ramda";
import {DRINK_SIZES, DrinkSize, DocumentDrinkSizesIndex, WineItem} from "features/editor/MenuDocumentModel";
import {FormSelect} from "components/UIComponents/FormSelect";
import {FormInput} from "components/UIComponents/FormInput";
import {menuEditorActions, menuEditorSelectors} from "features/editor/editorSlice";
import {useAppDispatch, useAppSelector} from "lib/redux/hooks";
import {EditorItem} from "features/editor/editorUtils";

export interface WinePriceEditorProps {
    wine: EditorItem<WineItem>
}

export function WinePriceEditor(props: WinePriceEditorProps): ReactElement {
    const dispatch = useAppDispatch();
    const document = useAppSelector(menuEditorSelectors.document);

    const itemKey = props.wine.itemKey;
    const wineItem = props.wine.menuItem;

    function updateProperty<K extends string, T>(property: K, value: T) {
        dispatch(menuEditorActions.editItem({
            key: itemKey,
            newItem: assoc<T, WineItem, K>(property, value, wineItem) as unknown as WineItem
        }));
    }

    const updateSize = (index: DocumentDrinkSizesIndex, size: DrinkSize) => {
        const sizes = document?.sizes;
        if (sizes == null) {
            return;
        }
        const newSizes = assocPath([index], size, sizes);
        dispatch(menuEditorActions.setSizes(newSizes));
    }

    const updatePrice = (index: number, price: number | null) => {
        const winePrices = wineItem.prices ?? [null, null, null];
        const newWinePrices = assocPath([index], price, winePrices);
        updateProperty('prices', newWinePrices);
    }

    return (
        <>
            {wineItem.purchasePricePerBottle != null && (
                <EditorGroup>
                    <EditorGroupTitle>
                        <FormattedMessage id="editor.wine.price.purchasePrice"/>
                    </EditorGroupTitle>
                    <div className="grid grid-cols-2">
                        <div className="text-left">
                            {wineItem.size}cl
                        </div>
                        <div className="text-right font-bold pr-1">
                            <FormattedNumber
                                value={wineItem.purchasePricePerBottle}
                                minimumFractionDigits={2}
                                maximumFractionDigits={2}
                            />
                        </div>
                    </div>
                </EditorGroup>
            )}
            <EditorGroup>
                <EditorGroupTitle>
                    <FormattedMessage id="editor.wine.price.salePrice"/>
                </EditorGroupTitle>
                {(range(0, 4) as DocumentDrinkSizesIndex[]).map(index  => {
                    if (document == null) {
                        return <></>;
                    }

                    return (
                        <PriceInput
                            key={index}
                            size={document.sizes[index]}
                            priceValue={wineItem.prices[index] ?? null}
                            onChangeSize={s => updateSize(index, s)}
                            onChangePrice={p => updatePrice(index, p)}
                        />
                    );
                })}
            </EditorGroup>
        </>
    );
}

interface PriceInputProps {
    size: DrinkSize
    priceValue: number | null
    onChangeSize: (size: DrinkSize) => void
    onChangePrice: (price: number | null) => void
}

function PriceInput(props: PriceInputProps): ReactElement {
    const [priceValue, setPriceValue] = useState<string>("");
    const propsPriceValue = props.priceValue;
    useEffect(() => {
        setPriceValue(propsPriceValue?.toString() ?? "");
    }, [propsPriceValue]);

    const updateSize = (size: DrinkSize) => {
        props.onChangeSize(size);
    }
    const updatePrice = (p: string) => {
        setPriceValue(p);

        if (isEmpty(p.trim())) {
            props.onChangePrice(null);
            return;
        }

        try {
            const price = parseFloat(p);
            props.onChangePrice(price);
        } catch (e) {
            // not a parseable float => do nothing
        }
    };
    return (
        <div className="grid grid-cols-2 items-center gap-3">
            <div className="flex-auto">
                <FormSelect
                    options={DRINK_SIZES}
                    selected={props.size}
                    onChange={updateSize}
                    getLabel={identity}
                />
            </div>
            <div className="flex-auto">
                <FormInput
                    value={priceValue}
                    type="number"
                    onChange={e => updatePrice(e.target.value)}
                />
            </div>
        </div>
    );
}
